/** Currency symbol for Agave's virtual credits. */
export const CREDIT = '₳';

const creditFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 2,
});

/** `1234.5` → `"1,234.50"` */
export function formatNumber(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '0.00';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** `1234.5` → `"₳1,234.50"` */
export function formatCredits(value: number, withSymbol = true): string {
  const formatted = creditFormatter.format(Number.isFinite(value) ? value : 0);
  return withSymbol ? `${CREDIT}${formatted}` : formatted;
}

/** `1_250_000` → `"1.25M"` */
export function formatCompact(value: number): string {
  return compactFormatter.format(Number.isFinite(value) ? value : 0);
}

/** `1234.5` → `"₳1.25K"` */
export function formatCreditsCompact(value: number): string {
  return `${CREDIT}${compactFormatter.format(Number.isFinite(value) ? value : 0)}`;
}

export function formatMultiplier(value: number): string {
  return `${value.toFixed(2)}x`;
}

export function formatPercent(value: number, decimals = 2): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/** Signed display for session P/L: `+₳12.00` / `-₳4.50`. */
export function formatDelta(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatCredits(Math.abs(value))}`;
}

export function formatTime(date: Date | string | number): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

export function formatDateTime(date: Date | string | number): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function relativeTime(date: Date | string | number): string {
  const d = date instanceof Date ? date : new Date(date);
  const seconds = Math.round((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

/** Convert a numeric string with optional decimals into a bet amount. */
export function parseAmount(input: string): number {
  const cleaned = input.replace(/[^0-9.]/g, '');
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : 0;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Round to the nearest valid step (prevents floating point dust on bets). */
export function roundToStep(value: number, step: number): number {
  if (step <= 0) return value;
  return Math.round(value / step) * step;
}
